import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api.js";
import { formatDate, StatusBadge } from "../ui.js";
import type { GenerationBatch } from "../types.js";
import { PromptSourceBlocks } from "../components/prompt-source-blocks.js";

const active = new Set(["queued", "processing"]);

export function GenerationsScreen() {
  const { batchId } = useParams();
  const [batches, setBatches] = useState<GenerationBatch[]>([]);
  const [detail, setDetail] = useState<GenerationBatch | null>(null);
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("all");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      if (batchId) setDetail(await api.getGeneration(batchId));
      else
        setBatches(
          (await api.listGenerations(new URLSearchParams({ pageSize: "100" })))
            .items,
        );
      setError("");
    } catch {
      setError("Не удалось загрузить генерации.");
    }
  }, [batchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const shouldPoll = batchId
    ? Boolean(detail && active.has(detail.status))
    : batches.some((batch) => active.has(batch.status));
  useEffect(() => {
    if (!shouldPoll) return;
    const timer = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(timer);
  }, [load, shouldPoll]);

  const clients = useMemo(
    () =>
      Array.from(
        new Map(
          batches.map((batch) => [batch.clientId, batch.clientName]),
        ).entries(),
      ).filter((entry): entry is [string, string] => entry[0] !== null),
    [batches],
  );
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("ru");
    const cutoff =
      period === "all" ? 0 : Date.now() - Number(period) * 86400000;
    return batches.filter((batch) => {
      const matchesSearch =
        !query ||
        [batch.id, batch.clientName, batch.templateName]
          .join(" ")
          .toLocaleLowerCase("ru")
          .includes(query);
      return (
        matchesSearch &&
        (clientId === "all" || batch.clientId === clientId) &&
        (status === "all" || batch.status === status) &&
        (!cutoff || new Date(batch.createdAt).getTime() >= cutoff)
      );
    });
  }, [batches, clientId, period, search, status]);

  if (batchId)
    return <BatchDetail batch={detail} error={error} reload={load} />;
  return (
    <section className="panel release-table-panel generations-page">
      <div className="page-heading">
        <div>
          <h1>Генерации</h1>
          <p>История созданных пакетов изображений</p>
        </div>
        <Link className="button primary compact-button" to="/create">
          ＋ Создать изображение
        </Link>
      </div>
      <div className="table-toolbar generation-toolbar">
        <label className="search-field">
          ⌕
          <input
            aria-label="Поиск генераций"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Название, клиент или ID"
          />
        </label>
        <select
          aria-label="Клиент генераций"
          value={clientId}
          onChange={(event) => setClientId(event.target.value)}
        >
          <option value="all">Все клиенты</option>
          {clients.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select
          aria-label="Статус генераций"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="all">Все статусы</option>
          <option value="queued">В очереди</option>
          <option value="processing">В обработке</option>
          <option value="completed">Готово</option>
          <option value="partially_completed">Готово частично</option>
          <option value="failed">Ошибка</option>
          <option value="cancelled">Отменено</option>
        </select>
        <select
          aria-label="Период генераций"
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
        >
          <option value="all">За всё время</option>
          <option value="7">За 7 дней</option>
          <option value="30">За 30 дней</option>
          <option value="90">За 90 дней</option>
        </select>
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      <div className="release-table generation-table" role="table">
        <div className="release-row release-head" role="row">
          <span>Создано</span>
          <span>Клиент</span>
          <span>Шаблон</span>
          <span>Изображения</span>
          <span>Статус</span>
          <span />
        </div>
        {filtered.map((batch) => {
          const completed = batch.items.filter(
            (item) => item.status === "completed",
          ).length;
          return (
            <Link
              className="release-row"
              role="row"
              key={batch.id}
              to={`/generations/${batch.id}`}
            >
              <span>{formatDate(batch.createdAt, true)}</span>
              <span className="client-name compact-client">
                <i>{batch.clientName.slice(0, 2).toUpperCase()}</i>
                <strong>{batch.clientName}</strong>
              </span>
              <span>
                <strong>{batch.templateName}</strong>
                <small>{batch.id.slice(0, 8)}</small>
              </span>
              <span>
                {completed} / {batch.requestedCount}
              </span>
              <span>
                <StatusBadge value={batch.status} />
              </span>
              <span className="row-actions">›</span>
            </Link>
          );
        })}
        {!filtered.length ? (
          <div className="table-message">Генерации не найдены</div>
        ) : null}
      </div>
    </section>
  );
}

function BatchDetail({
  batch,
  error,
  reload,
}: {
  batch: GenerationBatch | null;
  error: string;
  reload: () => Promise<void>;
}) {
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  if (error)
    return (
      <section className="panel">
        <div className="form-error">{error}</div>
      </section>
    );
  if (!batch) return <section className="panel">Загружаем генерацию…</section>;
  return (
    <section className="panel generation-detail">
      <div className="section-heading">
        <div>
          <Link to="/generations">← Все генерации</Link>
          <h1>
            {batch.clientName} · {batch.templateName}
          </h1>
          <p>
            {batch.platformSnapshot.name} · {batch.platformSnapshot.width} ×{" "}
            {batch.platformSnapshot.height} ·{" "}
            {formatDate(batch.createdAt, true)}
          </p>
        </div>
        <div className="batch-actions">
          <Link
            className="button primary compact-button"
            to={`/create?from=${batch.id}`}
          >
            Создать на основе
          </Link>
          <StatusBadge value={batch.status} />
          {active.has(batch.status) ? (
            <button
              className="danger"
              onClick={() => void api.cancelGeneration(batch.id).then(reload)}
            >
              Отменить генерацию
            </button>
          ) : null}
        </div>
      </div>
      {active.has(batch.status) ? (
        <div className="notice">
          Уже отправленный запрос к провайдеру может завершиться после отмены.
          Его результат будет отброшен и не появится в галерее.
        </div>
      ) : null}
      {batch.promptSnapshot ? (
        <section
          className="generation-final-prompt"
          aria-label="Итоговый запрос"
        >
          <header>
            <div>
              <h2>Итоговый запрос</h2>
              <p>
                Полная инструкция с подписями источников. При копировании
                сохраняется исходный текст без подписей.
              </p>
            </div>
            <button
              type="button"
              className="secondary compact-button"
              onClick={() => {
                void navigator.clipboard.writeText(batch.promptSnapshot ?? "");
                setCopiedPrompt(true);
                window.setTimeout(() => setCopiedPrompt(false), 1800);
              }}
            >
              {copiedPrompt ? "Скопировано" : "Скопировать"}
            </button>
          </header>
          <div className="generation-final-prompt-text">
            <PromptSourceBlocks
              prompt={batch.promptSnapshot}
              blocks={batch.promptBlocks}
            />
          </div>
        </section>
      ) : null}
      <div className="generation-cards">
        {batch.items.map((item) => (
          <article key={item.id} className="generation-card">
            {item.thumbnailId ? (
              <img
                src={`/api/v1/generation-items/${item.id}/original`}
                alt={`Вариант ${item.itemNumber}`}
              />
            ) : (
              <div className="generation-placeholder">
                {item.status === "processing" ? "◌" : "✦"}
              </div>
            )}
            <div>
              <strong>Вариант {item.itemNumber}</strong>
              <StatusBadge value={item.status} />
              <small>
                Попыток: {item.attemptCount}/{item.maxAttempts}
              </small>
              {item.errorCode ? <code>{item.errorCode}</code> : null}
              <div className="card-actions">
                {item.status === "completed" ? (
                  <>
                    <a
                      href={`/api/v1/generation-items/${item.id}/original`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Открыть оригинал
                    </a>
                    <a
                      href={`/api/v1/generation-items/${item.id}/download-original`}
                    >
                      Скачать оригинал
                    </a>
                    <a
                      href={`/api/v1/generation-items/${item.id}/download-webp`}
                      title="WebP из оригинала, без изменения размеров и новой генерации"
                    >
                      Сохранить в WebP
                    </a>
                  </>
                ) : null}
                {item.status === "failed" ? (
                  <button
                    onClick={() =>
                      void api.retryGenerationItem(item.id).then(reload)
                    }
                  >
                    Повторить
                  </button>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
